import {useEffect, useState} from "react";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {SidebarProvider, SidebarTrigger} from "./components/basic/sidebar";
import {Menu, Shield} from "lucide-react";
import {Toaster} from "./components/basic/sonner";
import {Badge} from "./components/basic/badge";
import {ErrorNotification} from "./components/shared/ErrorNotification";
import {AlumniActivatedBanner} from "./components/shared/AlumniActivatedBanner";
import {AlumniBanner} from "./components/shared/AlumniBanner";
import {BannerStack} from "./components/shared/banners/BannerStack";
import {HeroBannerSlot} from "./components/shared/banners/HeroBannerSlot";
import {QuotaBanner} from "./components/shared/QuotaBanner";
import {SemesterSetupBanner} from "./components/shared/SemesterSetupBanner";
import {useQuotaWarning} from "./hooks/useQuotaWarning.ts";
import {useSemesterSetupPrompt} from "./hooks/useSemesterSetupPrompt.ts";
import {useAuth} from "./hooks/useAuth.ts";
import {UserSidebar} from "./components/layout/UserSidebar";
import {AdminSidebar} from "./components/layout/AdminSidebar";
import {logger} from "./utils/logger";
import {useTranslationWithKeys} from "./hooks/useTranslationWithKeys";
import {mapBackendLanguageToI18nLocale} from "./utils/localeUtils";
import {usePersistShownLanguageIfUnset} from "./hooks/usePersistShownLanguageIfUnset.ts";
import TranslationToggle from "@/components/shared/TranslationToggle/TranslationToggle";
import {LanguageToggle} from "@/components/shared/selectors/LanguageToggle/LanguageToggle";

export default function App() {
    const {user, logout, featureConfig} = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const {t, i18n} = useTranslationWithKeys();
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const [showNotification, setShowNotification] = useState(false);
    const [showAlumniBanner, setShowAlumniBanner] = useState(false);
    const [showAlumniActivatedBanner, setShowAlumniActivatedBanner] = useState(false);
    const [quotaBannerDismissed, setQuotaBannerDismissed] = useState(false);
    const [semesterBannerDismissed, setSemesterBannerDismissed] = useState(false);
    const quotaWarningLevel = useQuotaWarning();
    const needsSemesterSetup = useSemesterSetupPrompt();
    usePersistShownLanguageIfUnset(user, i18n.language);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const errorParam = searchParams.get('error_code');

        if (errorParam) {
            setErrorCode(errorParam);
            setShowNotification(true);
        }
    }, [location]);

    useEffect(() => {
        if (featureConfig?.isAlumniEligible) {
            setShowAlumniBanner(true);
        } else if (showAlumniBanner && featureConfig !== undefined) {
            setShowAlumniBanner(false);
        }
    }, [featureConfig?.isAlumniEligible]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (!user || searchParams.get('alumni') !== 'activated') {
            return;
        }
        setShowAlumniActivatedBanner(!!user.flags?.includes('IS_ALUMNI'));
        searchParams.delete('alumni');
        const query = searchParams.toString();
        navigate({pathname: location.pathname, search: query ? `?${query}` : ''}, {replace: true});
    }, [location.pathname, location.search, navigate, user]);

    const isAdminMode = location.pathname.startsWith('/admin');

    useEffect(() => {
        logger.log('Auth state:', {user: user?.email, location: location.pathname});
    }, [user, location]);

    useEffect(() => {
        if (user?.language) {
            const i18nLocale = mapBackendLanguageToI18nLocale(user.language);
            if (i18n.language !== i18nLocale) {
                i18n.changeLanguage(i18nLocale);
            }
        }
    }, [user?.language, i18n]);

    const handleEnterAdminMode = () => {
        if (user?.roles?.includes('PLATFORM_ADMIN')) {
            navigate("/admin/users");
        }
    };

    const handleExitAdminMode = () => {
        navigate("/dashboard");
    };

    return (
        <SidebarProvider defaultOpen={true}>
            <div className="flex min-h-screen w-full">
                <TranslationToggle/>
                {isAdminMode ? (
                        <AdminSidebar
                            location={location}
                            navigate={navigate}
                            onExitAdminMode={handleExitAdminMode}
                            onLogout={logout}
                        />
                    ) : (
                        <UserSidebar
                            user={user}
                            location={location}
                            navigate={navigate}
                            onEnterAdminMode={handleEnterAdminMode}
                            onLogout={logout}
                        />
                    )}

                    <main className="flex-1 min-w-0 md:ml-0">
                        <BannerStack>
                            {showNotification && errorCode && (
                                <ErrorNotification
                                    errorCode={errorCode}
                                    onClose={() => setShowNotification(false)}
                                />
                            )}
                            {quotaWarningLevel !== "none" && !quotaBannerDismissed && (
                                <QuotaBanner
                                    level={quotaWarningLevel}
                                    onClose={() => setQuotaBannerDismissed(true)}
                                />
                            )}
                            {needsSemesterSetup && !semesterBannerDismissed && (
                                <SemesterSetupBanner
                                    onClose={() => setSemesterBannerDismissed(true)}
                                />
                            )}
                        </BannerStack>
                        <HeroBannerSlot>
                            {showAlumniBanner && (
                                <AlumniBanner
                                    onClose={() => setShowAlumniBanner(false)}
                                />
                            )}
                            {showAlumniActivatedBanner && user?.email && (
                                <AlumniActivatedBanner
                                    email={user.email}
                                    onClose={() => setShowAlumniActivatedBanner(false)}
                                />
                            )}
                        </HeroBannerSlot>
                        <div className="border-b bg-card">
                            <div className="mx-auto flex w-full max-w-(--content-max-width) items-center gap-4 px-6 py-4">
                                <SidebarTrigger>
                                    <Menu className="h-5 w-5"/>
                                </SidebarTrigger>
                                <div className="flex-1">
                                    {isAdminMode ? (
                                        <div className="flex items-center gap-2">
                                            <p>{t('admin.adminPanel')}</p>
                                            <Badge variant="outline" className="gap-1">
                                                <Shield className="h-3 w-3"/>
                                                {t('admin.adminAccess')}
                                            </Badge>
                                        </div>
                                    ) : (
                                        <p>{t('dashboard.welcomeBack')}, {user?.firstName || user?.email || 'User'}!</p>
                                    )}
                                </div>
                                {!isAdminMode && (
                                    <div className="flex items-center gap-2">
                                        <LanguageToggle/>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mx-auto w-full max-w-(--content-max-width) p-6 md:p-8 lg:p-10">
                            <Outlet/>
                        </div>
                    </main>
                    <Toaster/>
                </div>
            </SidebarProvider>
    );
}
